import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MarketingOverviewModel } from '../../../types';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import {
  getAllMarketingOverviewsReact,
  UserKeys,
  resetAllMarketingModels,
} from '../../api/users-api';
import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../state/zustand-store';
import CreateEditMarketingBarDialog from './create-edit-marketing-bar-dialog';
import DeleteMarketingBarsDialog from './delete-marketing-bars-dialog';
import { handleErrors } from '../../utils/errors';
import { createGridOverlays } from '../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../shared/shared-data-grid-client';
import SharedToolbar from '../../shared/shared-toolbar-latest';
import SharedIconButton from '../../shared/buttons/shared-icon-button';
import CreateButton from '../../shared/buttons/create-button';

import Tooltip from '@mui/material/Tooltip';
import Edit from '@mui/icons-material/Edit';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export default function MarketingManagerPage() {
  const queryClient = useQueryClient();
  const [dialogType, setDialogType] = useState<'create' | 'update' | null>(
    null
  );
  const [selectedBar, setSelectedBar] = useState<MarketingOverviewModel | null>(
    null
  );
  const { setSnackbar } = useStore();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [openDeleteMarketingBar, setOpenDeleteMarketingBar] = useState(false);
  const [fakeLoading, setFakeLoading] = useState(false);

  const {
    data: marketingOverviews,
    isLoading: marketingOverviewsIsLoading,
    isFetching: marketingOverviewsIsFetching,
  } = useQuery({
    queryKey: [UserKeys.MarketingOverviewModels],
    queryFn: () => getAllMarketingOverviewsReact(),
    meta: {
      errorMessage:
        'There was an issue loading data. Please refresh and try again.',
    },
  });

  const { mutate } = useMutation({
    mutationFn: () => resetAllMarketingModels(),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Marketing Model Refresh was successful',
      });
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue refreshing marketing models. Please refresh and try again.',
      });
    },
  });

  useEffect(() => {
    if (fakeLoading) {
      setTimeout(() => {
        setFakeLoading(false);
      }, 2000);
    }
  }, [fakeLoading]);

  const columns: GridColDef<MarketingOverviewModel>[] = useMemo(
    () => [
      {
        field: 'dateCreated',
        headerName: 'Date Created',
        width: 185,
        flex: 1,
        type: 'dateTime',
        valueGetter: (value: number) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          return params.toLocaleString();
        },
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
      },
      {
        field: 'url',
        headerName: 'URL',
        flex: 1,
        valueFormatter: (params) => {
          if (params === '') {
            return 'N/A';
          } else {
            return params;
          }
        },
      },
      {
        field: 'active',
        headerName: 'Status',
        flex: 1,
        type: 'boolean',
        valueGetter: (value: string) => {
          return value;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          buttonArray.push(
            <GridActionsCellItem
              label="Edit"
              icon={
                <Tooltip arrow title={`Edit`}>
                  <Edit />
                </Tooltip>
              }
              onClick={() => {
                setSelectedBar(params.row);
                setDialogType('update');
              }}
            />
          );
          return buttonArray;
        },
      },
    ],
    [setSelectedBar, setDialogType]
  );

  function handleReset() {
    setFakeLoading(true);
    mutate();
  }

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        primaryButtons={
          <CreateButton
            onClick={() => setDialogType('create')}
            label="Add New"
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="reset"
              onClick={handleReset}
              isLoading={fakeLoading}
              tooltipLabel="Reset Marketing Models"
            />
            <SharedIconButton
              variant="delete"
              onClick={() => {
                setOpenDeleteMarketingBar(true);
              }}
              disabled={selectedRows.length === 0}
              tooltipLabel="Delete"
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                queryClient.invalidateQueries({
                  queryKey: [UserKeys.MarketingOverviewModels],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      ></SharedToolbar>
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Marketing Bars', <InfoOutlined />),
        }}
        checkboxSelection
        defaultGridState={{
          sorting: {
            sortModel: [
              {
                field: 'dateCreated',
                sort: 'desc',
              },
            ],
          },
        }}
        rows={marketingOverviews || []}
        getRowId={(row) => row.id}
        columns={columns}
        loading={marketingOverviewsIsLoading || marketingOverviewsIsFetching}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
      {dialogType === 'create' ? (
        <CreateEditMarketingBarDialog
          open={dialogType !== null}
          handleClose={() => setDialogType(null)}
          dialogType={dialogType}
          selectedBar={null}
        />
      ) : null}

      {dialogType === 'update' && selectedBar ? (
        <CreateEditMarketingBarDialog
          open={dialogType !== null}
          handleClose={() => {
            setDialogType(null);
            setSelectedBar(null);
          }}
          dialogType={dialogType}
          selectedBar={selectedBar}
        />
      ) : null}

      {selectedRows.length > 0 &&
      openDeleteMarketingBar &&
      marketingOverviews ? (
        <DeleteMarketingBarsDialog
          open={openDeleteMarketingBar}
          handleClose={() => {
            setOpenDeleteMarketingBar(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      ) : null}
    </>
  );
}
